import React, { ErrorInfo, PureComponent } from "react";
import * as Sentry from "@sentry/browser";
import { Accordion, Button, TitleHeader } from "@xapp/components";
import style from "./style.scss";

export interface ErrorBoundaryStateProps { }
export interface ErrorBoundaryOwnProps {
    children?: JSX.Element[] | JSX.Element;
}

export type ErrorBoundaryProps = ErrorBoundaryStateProps & ErrorBoundaryOwnProps;

export interface ErrorBoundaryState {
    error?: Error;
    info?: ErrorInfo;
}

export const DOCUMENTATION_URL = window.xaStudioConfig?.documentationUrl || "https://documentation.xapp.ai";

export class ErrorBoundaryView extends PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = {};

        this.handleErrorEvent = this.handleErrorEvent.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
    }

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidMount() {
        window.addEventListener("error", this.handleErrorEvent);
    }

    componentWillUnmount() {
        window.removeEventListener("error", this.handleErrorEvent);
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // ensure the error is being reported to sentry
        Sentry.captureException(error);

        this.setState({
            error,
            info
        });
    }

    /**
     * prevents the error from bubbling up to the virtual console in tests
     * @param e
     */
    handleErrorEvent(e: Event) {
        if (__ENV__ === "test") {
            e.preventDefault();
        }
    }

    handleRefreshClick() {
        window.location.reload();
    }

    render() {
        const { error } = this.state;

        if (error) {
            return this.renderError();
        }

        return this.props.children;
    }

    renderDevError() {
        const { error } = this.state;

        return (
            <>
                <Accordion title="Show Error Details">
                    <h2>Error</h2>
                    <pre className={style.pre}>{error.toString()}</pre>
                    <pre className={style.pre} dangerouslySetInnerHTML={this.renderComponentStack()} />
                </Accordion>
            </>
        );
    }

    renderError() {
        return (
            <section className={style.wrap}>
                <TitleHeader static value="Oh no! Something's not right here." />

                {this.renderProdError()}
                {this.renderDevError()}
            </section>
        );
    }

    renderComponentStack() {
        if (this.state.info) {
            return {
                __html: this.state.info.componentStack.trim()
            };
        }
    }

    renderProdError() {
        return (
            <>
                <p>
                    There's a good chance that this was a one-time thing. Please{" "}
                    <button onClick={this.handleRefreshClick}>
                        refresh the page
                    </button>{" "}
                    and try again.
                </p>

                <p>
                    If that's not the case, and you are shown this screen over and over, fear not: we are here to help
                </p>

                <footer className={style.cta}>
                    <a target="_blank" rel="noopener noreferrer" href={DOCUMENTATION_URL}>
                        <Button variation="primary" label="Take me to Support" />
                    </a>
                </footer>
            </>
        );
    }
}
